// Settle-sheet prop assembly (gdn-2957).
//
// The drawer was never just chrome: it was the layer that turned a small
// trigger payload ({patientId, encounterId, method, amount}) into the
// eighteen props SettleSheet actually needs — invoiceable line ids,
// writable line ids, charge groups, the unallocated credit, the
// BE-suggested reason, the eligibility copay chip.
//
// Retiring the drawer means that assembly has to live somewhere that
// isn't the drawer. It lives here, and both the drawer and the
// page-anchored PaymentSheetHost consume it, so there is exactly one
// implementation to keep correct.

import { useMemo } from 'react';
import type { DisplayEncounterChargeGroup } from '@features/billing/components/charge-groups';
import { displayGroupsFromPreview } from '@features/billing/components/charge-groups';
// Direct file import (no feature barrel) — same source the Eligibility tab uses.
import { useEligibilityBadgeState } from '@features/eligibility-checks/hooks/useEligibilityBadgeState';

import type { SettleMethod } from '../types';
import type { CopaySuggestion } from '../utils/allocationEngine';

import { useLedgerData } from './useLedgerData';
import {
  type CreditSuggestion,
  creditSuggestionOf,
} from '@features/billing/utils/creditSuggestion';
import {
  type OpenInvoiceTarget,
  openInvoiceTargeting,
} from '@features/billing/utils/openInvoiceTargeting';

interface UseSettleSheetPropsArgs {
  patientId: string;
  encounterId?: string | null;
  /** Exact charge lines the settle action should cover, when the caller knows. */
  chargeLineIds?: string[];
}

export interface SettleSheetDerived {
  /** Full ledger read — the drawer still renders its own chrome off this. */
  ledger: ReturnType<typeof useLedgerData>;
  /** Props destined for `<SettleSheet>`, minus the caller's own presets. */
  sheet: {
    suggestedReason?: string;
    invoiceChargeLineIds?: string[];
    writableChargeLineIds?: string[];
    invoiceableAmountCents?: number;
    unallocatedCreditCents: number;
    chargeGroups: DisplayEncounterChargeGroup[];
    reminderInvoiceId?: string;
    /** The invoice a card charge should settle, or null when charging
     * one is not the right act. Never undefined here: the hook always
     * has an opinion, because it can see the ledger. */
    openInvoiceTarget: OpenInvoiceTarget | null;
    /** Unapplied credit worth mentioning before collecting more, or null.
     * Derived here rather than passed by callers so every surface that
     * opens the sheet gets it — the same reason openInvoiceTarget is
     * derived here (gdn-3169). */
    creditSuggestion: CreditSuggestion | null;
    copaySuggestion: CopaySuggestion | null;
    /** Charge-line preview still in flight — the sheet holds space for it. */
    chargeGroupsLoading: boolean;
  };
}

/**
 * Assemble everything SettleSheet needs for a given patient/encounter scope.
 *
 * Pure derivation over `useLedgerData` + the eligibility snapshot — no
 * drawer state, no tab state, nothing that assumes a side pane.
 */
export function useSettleSheetProps({
  patientId,
  encounterId,
  chargeLineIds,
}: UseSettleSheetPropsArgs): SettleSheetDerived {
  const ledger = useLedgerData({ patientId, encounterId, chargeLineIds });

  // Eligibility snapshot — same RTK-cached source the Eligibility tab reads.
  // Feeds the settle sheet's copay suggestion chip (amount + checked-at);
  // the production EligibilityCheckResponse doesn't expose a plan name.
  const { eligibilityData } = useEligibilityBadgeState({
    patientId,
    encounterId: encounterId ?? undefined,
  });
  const copaySuggestion = useMemo<CopaySuggestion | null>(() => {
    const financials: Record<string, unknown> | undefined =
      eligibilityData?.financials;
    const rawCopay = financials?.copayment_cents;
    if (typeof rawCopay !== 'number' || rawCopay <= 0) return null;
    return {
      amountCents: Math.round(rawCopay),
      checkedAt: eligibilityData?.last_checked_at ?? null,
    };
  }, [eligibilityData]);

  const preview = ledger.preview;
  const openInvoiceIds = ledger.openInvoiceIds;
  const openInvoices = ledger.openInvoices;
  const accountAllocatableCreditCents = ledger.accountAllocatableCreditCents;
  const isPreviewLoading = ledger.isPreviewLoading;

  const sheet = useMemo(() => {
    const previewLines = preview?.encounters?.flatMap(
      encounter => encounter.lines ?? [],
    );
    const explicitChargeLineIds =
      chargeLineIds && chargeLineIds.length > 0 ? new Set(chargeLineIds) : null;

    const invoiceableChargeLineIds =
      previewLines
        ?.filter(line => {
          if ((line.patient_due_cents ?? 0) <= 0 || line.already_invoiced) return false;
          return !explicitChargeLineIds || explicitChargeLineIds.has(line.charge_line_id);
        })
        .map(line => line.charge_line_id) ?? chargeLineIds;

    const invoiceableLineAmountCents = previewLines?.reduce((total, line) => {
      if ((line.patient_due_cents ?? 0) <= 0 || line.already_invoiced) return total;
      if (explicitChargeLineIds && !explicitChargeLineIds.has(line.charge_line_id)) {
        return total;
      }
      return total + (line.patient_due_cents ?? 0);
    }, 0);

    // Write-off may target invoiced lines too, so this set is deliberately
    // wider than the invoiceable one.
    const selectableWriteOffChargeLineIds =
      previewLines
        ?.filter(line => {
          if ((line.patient_due_cents ?? 0) <= 0) return false;
          return !explicitChargeLineIds || explicitChargeLineIds.has(line.charge_line_id);
        })
        .map(line => line.charge_line_id) ?? chargeLineIds;

    // Everything owed already sits on an open invoice, so a card charge
    // must settle THAT invoice rather than mint a second one (gdn-3154).
    // Derived here, not at one call site: the gated sheet and the legacy
    // drawer render the same SettleSheet, and fixing only the surface
    // that reported the bug leaves the other one double-billing.
    //
    // `preview` must have arrived first. Without it invoiceableChargeLineIds
    // falls back to the caller's ids, and an empty list would read as
    // "nothing left to invoice" when the truth is "not known yet".
    return {
      creditSuggestion: creditSuggestionOf(accountAllocatableCreditCents),
      openInvoiceTarget: openInvoiceTargeting(
        openInvoices,
        !!preview && (invoiceableChargeLineIds?.length ?? 0) === 0,
      ),
      suggestedReason: preview?.suggested_reason,
      invoiceChargeLineIds: invoiceableChargeLineIds,
      writableChargeLineIds: selectableWriteOffChargeLineIds,
      invoiceableAmountCents: preview?.total_due_cents ?? invoiceableLineAmountCents,
      unallocatedCreditCents: preview?.unallocated_credit_cents ?? 0,
      chargeGroups: displayGroupsFromPreview(preview),
      reminderInvoiceId: openInvoiceIds[0],
      copaySuggestion,
      chargeGroupsLoading: isPreviewLoading,
    };
  }, [
    preview,
    chargeLineIds,
    openInvoiceIds,
    openInvoices,
    accountAllocatableCreditCents,
    copaySuggestion,
    isPreviewLoading,
  ]);

  return { ledger, sheet };
}

/**
 * Opening amount for the settle sheet.
 *
 * Invoice-like methods treat the amount as a NEW ad-hoc charge — selected
 * receipt lines add on top inside the sheet — so only an explicit
 * caller-provided amount may prefill it. Prefilling the balance there
 * mints a duplicate charge (double bill).
 */
export function settleAmountFor(
  method: SettleMethod | undefined,
  defaultAmountCents: number | undefined,
  balanceCents: number,
): number {
  if (method === 'invoice' || method === 'card_on_file') {
    return defaultAmountCents ?? 0;
  }
  return defaultAmountCents ?? (balanceCents > 0 ? balanceCents : 0);
}
